import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { PackageOpen, ShieldCheck, Sparkles, Trophy, Coins, Globe } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';

const VALUES = [
  { icon: PackageOpen, title: 'Real Collectibles', desc: 'Every pack you open is backed by authentic, gradeable trading cards and premium collectibles shipped straight to your door.' },
  { icon: ShieldCheck, title: 'Provably Fair', desc: 'Transparent, adjustable gacha probabilities and verifiable pulls — you always know your odds before you play.' },
  { icon: Trophy, title: 'Chase the Legends', desc: 'From common hits to mythic grails graded GEM MT 10, hunt the rarest cards and build a collection worth showing off.' },
  { icon: Coins, title: 'Your Rewards, Your Choice', desc: 'Keep your cards, ship them home, or cash out — Cardtopia puts the value of every pull back in your hands.' },
];

const STEPS = [
  { n: '01', title: 'Pick a Pack', desc: 'Browse curated packs and rips across Pokémon, One Piece, gadgets and more.' },
  { n: '02', title: 'Open & Reveal', desc: 'Experience cinematic pack openings and discover what you pulled in real time.' },
  { n: '03', title: 'Claim or Cash Out', desc: 'Ship your collectibles, add them to your vault, or convert rewards instantly.' },
];

export default function About() {
  const navigate = useNavigate();
  return (
    <>
      <SEOHead page="about" customTitle="About Us | Cardtopia" customDescription="Learn about Cardtopia — the premium gamified trading card and collectibles marketplace." />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full bg-[#FFD400]/30 blur-[130px]" />
        <div className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full bg-[#FF8C00]/20 blur-[130px]" />
        <div className="relative max-w-[1100px] mx-auto px-4 lg:px-8 pt-14 pb-10 text-center">
          <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFF3D6] border border-[#F0E3C8] text-[#FF6A00] font-bold text-xs uppercase tracking-widest mb-5">
            <Sparkles size={13} /> About Cardtopia
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-[#1A1206] font-['Unbounded'] leading-[0.95]">
            Open Packs.<br /><span className="text-[#FF6A00]">Chase Legends.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
            className="mt-5 text-[#6E6452] text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Cardtopia is a premium, gamified marketplace for trading cards and collectibles. We blend the thrill of
            opening packs with real, ownable rewards — powered by fair mechanics and a global community of collectors.
          </motion.p>
        </div>
      </section>

      {/* Mission */}
      <section className="max-w-[1100px] mx-auto px-4 lg:px-8 py-10">
        <div className="bg-white border border-[#F0E3C8] rounded-3xl p-8 lg:p-12 shadow-[0_12px_40px_-20px_rgba(255,140,0,0.4)]">
          <h2 className="text-2xl lg:text-3xl font-black uppercase text-[#1A1206] font-['Unbounded'] mb-4">Our Mission</h2>
          <p className="text-[#6E6452] leading-relaxed text-base lg:text-lg">
            We believe collecting should be exciting, transparent, and rewarding. Cardtopia recreates the joy of ripping
            a fresh pack — the anticipation, the reveal, the chase for that one grail card — while guaranteeing that
            every reward is real and yours to keep. No gimmicks, just great pulls.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-[1100px] mx-auto px-4 lg:px-8 py-6">
        <div className="grid sm:grid-cols-2 gap-4 lg:gap-6">
          {VALUES.map((v, i) => (
            <motion.div key={v.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="bg-white border border-[#F0E3C8] rounded-2xl p-6 hover:border-[#FF8C00] transition-colors" data-testid={`about-value-${i}`}>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FFD400] to-[#FF8C00] flex items-center justify-center mb-4">
                <v.icon size={22} className="text-[#1A1206]" />
              </div>
              <h3 className="text-lg font-bold text-[#1A1206] mb-1.5 font-['Unbounded']">{v.title}</h3>
              <p className="text-[#6E6452] text-sm leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-[1100px] mx-auto px-4 lg:px-8 py-10">
        <h2 className="text-2xl lg:text-3xl font-black uppercase text-[#1A1206] font-['Unbounded'] mb-8 text-center">How It Works</h2>
        <div className="grid sm:grid-cols-3 gap-4 lg:gap-6">
          {STEPS.map((s, i) => (
            <motion.div key={s.n} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="relative bg-[#FFF8EA] border border-[#F0E3C8] rounded-2xl p-6">
              <span className="text-4xl font-black text-[#FF8C00]/40 font-['Unbounded']">{s.n}</span>
              <h3 className="text-lg font-bold text-[#1A1206] mt-2 mb-1.5">{s.title}</h3>
              <p className="text-[#6E6452] text-sm leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Community + CTA */}
      <section className="max-w-[1100px] mx-auto px-4 lg:px-8 pt-4 pb-16">
        <div className="relative overflow-hidden bg-gradient-to-r from-[#FFD400] to-[#FF6A00] rounded-3xl p-8 lg:p-12 text-center">
          <Globe size={40} className="text-[#1A1206] mx-auto mb-4" />
          <h2 className="text-2xl lg:text-4xl font-black uppercase text-[#1A1206] font-['Unbounded'] mb-3">Join the Community</h2>
          <p className="text-[#1A1206]/80 max-w-xl mx-auto mb-6 font-medium">
            Thousands of collectors are already opening packs and chasing legends. Your next grail is one pull away.
          </p>
          <button onClick={() => navigate('/pools')} data-testid="about-cta-open-packs"
            className="inline-flex items-center gap-2 h-12 px-8 rounded-full font-extrabold bg-[#1A1206] text-white hover:scale-105 transition-transform">
            <Sparkles size={18} /> Start Opening Packs
          </button>
        </div>
      </section>
    </>
  );
}
